<script lang="ts" setup>
    import { ref, watchEffect } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useContextStore } from '~~/stores/context'
    import type { ProviderName } from '#shared/providers'

    const contextStore = useContextStore()
    const { allContexts } = storeToRefs(contextStore)

    function providerLabel(providerName: ProviderName): string {
        return contextStore.providerLabel(providerName)
    }

    interface ScopeItem {
        name: string
        content: string
        label?: string
    }

    interface ContextItem {
        providerName: ProviderName
        providerLabel?: string
        identityLabel?: string
        scopes: ScopeItem[]
    }

    // Cache of resolved dynamic scope labels: key = "providerName:scopeName:content"
    const dynamicLabels = ref<Map<string, string | null>>(new Map())

    watchEffect(() => {
        for (const ctx of allContexts.value) {
            for (const scope of ctx.scopes) {
                if (scope.label !== undefined) {
                    continue
                }

                const key = `${ctx.providerName}:${scope.name}:${scope.content}`

                if (dynamicLabels.value.has(key)) {
                    continue
                }

                contextStore.getDynamicScopeLabel(ctx.providerName, scope.name, scope.content).then((label) => {
                    dynamicLabels.value = new Map(dynamicLabels.value).set(key, label ?? null)
                })
            }
        }
    })

    function isDynamicAndUnresolved(ctx: ContextItem, scope: ScopeItem): boolean {
        if (scope.label !== undefined) {
            return false
        }

        const key = `${ctx.providerName}:${scope.name}:${scope.content}`

        return !dynamicLabels.value.has(key)
    }

    function resolveScopeLabel(ctx: ContextItem, scope: ScopeItem): string {
        if (scope.label !== undefined) {
            return scope.label
        }

        const key = `${ctx.providerName}:${scope.name}:${scope.content}`
        const resolved = dynamicLabels.value.get(key)

        return resolved ?? ''
    }

    function sortedScopes(scopes: ScopeItem[]): ScopeItem[] {
        return [...scopes].sort((a, b) => a.name.localeCompare(b.name))
    }
</script>

<template>
    <div>
        <div
            v-if="allContexts.length === 0"
            class="text-caption q-pa-sm text-grey"
        >
            No active contexts
        </div>
        <q-list
            v-else
            dense
        >
            <template
                v-for="(ctx, idx) in allContexts"
                :key="ctx.providerName"
            >
                <q-separator v-if="idx > 0" />
                <q-item>
                    <q-item-section>
                        <q-item-label class="text-weight-medium">
                            {{ providerLabel(ctx.providerName) }}: {{ ctx.identityLabel || '(No identity)' }}
                        </q-item-label>
                        <q-item-label v-if="(ctx as any).profileLabel" caption>
                            Profile: {{ (ctx as any).profileLabel }}
                        </q-item-label>
                        <q-item-label
                            v-for="scope in sortedScopes(ctx.scopes)"
                            :key="scope.name"
                            caption
                        >
                            {{ scope.label ?? scope.name }}:
                            <picker-display
                                :picker-name="(scope as any).picker ?? 'text'"
                                :value="scope.content"
                            />
                        </q-item-label>
                    </q-item-section>
                </q-item>
            </template>
        </q-list>
    </div>
</template>
